/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { ChatBubbleIcon, Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { formatLocalizedDateTime } from '@/shared/utils/date';
import type { DealEvent } from '@/services/requests/housien-services/DealsService';
import { DEAL_PANEL_CLASS, DealPanelLabel } from './DealPanel';
import { toTimelineEntries, type TimelineEntry } from '../utils/deal-events';
import { ageOf } from './DealTimelineSidebar';

interface DealContactsPanelProps {
  events: DealEvent[];
  /** Opens the contact form from the panel header. */
  onRecordContact?: () => void;
}

/**
 * Empty state for an attendance nobody has logged a contact on yet.
 */
function ContactsEmpty() {
  const { t } = useTranslation('features/deals');
  return (
    <div data-testid="deal-contacts-empty" className="flex flex-col items-start gap-(--space-2) py-(--space-4)">
      <div className="flex w-full flex-col gap-(--space-2) opacity-50">
        <div className="h-2 w-1/2 rounded-full bg-(--surface-tertiary)" />
        <div className="h-2 w-3/4 rounded-full bg-(--surface-tertiary)" />
      </div>
      <Text component="span" variant="small" c="var(--text-secondary)" className="mt-(--space-2)">
        {t('detail.contacts.empty')}
      </Text>
      <Text component="span" variant="meta" c="var(--text-tertiary-soft)" className="leading-normal">
        {t('detail.contacts.emptyHint')}
      </Text>
    </div>
  );
}

/**
 * The attendance's contacts, isolated from the general timeline.
 *
 * The timeline mixes stage moves, proposals and system events with the
 * broker's own contact notes -- the more of those pile up, the harder it
 * is to answer "when did we last actually talk to this person". This
 * panel filters the same event list down to `contact_recorded`.
 *
 * Rows carry a channel medallion rather than a rail: there is no
 * sequence to trace here, only a list of conversations, and the icon is
 * what a reader scans for when looking for the call among the messages.
 *
 * @param props.events - The deal's raw event list, any order.
 * @param props.onRecordContact - Opens the contact form from the header.
 * @returns The contacts panel.
 */
export function DealContactsPanel({ events, onRecordContact }: DealContactsPanelProps) {
  const { t } = useTranslation('features/deals');
  const entries = toTimelineEntries(
    events.filter((event) => event.type === 'contact_recorded'),
  );

  return (
    <div className={cn(DEAL_PANEL_CLASS, 'gap-(--space-3)')}>
      <DealPanelLabel
        accessory={onRecordContact ? (
          <UnstyledButton
            type="button"
            onClick={onRecordContact}
            data-testid="contacts-record-contact"
            className="h-[34px] whitespace-nowrap rounded-(--radius-md) border border-solid border-(--border-medium) bg-(--surface-primary) px-(--space-3) [font-weight:var(--font-semibold)] text-(--text-primary) cursor-pointer hover:bg-(--surface-hover)"
            style={{ fontSize: 'var(--text-meta)' }}
          >
            {t('detail.actions.recordContact')}
          </UnstyledButton>
        ) : undefined}
      >
        {t('detail.contacts.title')}
      </DealPanelLabel>

      {entries.length === 0 ? <ContactsEmpty /> : (
        <div role="list" data-testid="deal-contacts-panel" className="flex flex-col gap-(--space-2)">
          {entries.map((entry) => (
            <ContactRow key={`${entry.event.timestamp}-${entry.event.type}`} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One recorded conversation: the channel medallion, what was said, and
 * how long ago.
 */
function ContactRow({ entry }: { entry: TimelineEntry }) {
  const { t } = useTranslation('features/deals');
  const when = ageOf(entry.event.timestamp, t);

  return (
    <div
      role="listitem"
      className="flex items-start gap-(--space-3) rounded-(--radius-md) border border-solid border-(--border-subtle) bg-(--surface-primary) px-(--space-3) py-(--space-2-5)"
    >
      <Text
        component="span"
        className="flex h-[28px] w-[28px] flex-none items-center justify-center rounded-(--radius-full) bg-(--surface-tertiary) text-(--seed-base)"
      >
        <ChatBubbleIcon width={14} height={14} />
      </Text>
      <div className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <Text
          component="span"
          variant="bodySm"
          c="var(--text-primary)"
          className="[font-weight:var(--font-semibold)]"
        >
          {entry.actor ?? t('detail.timeline.automatic')}
        </Text>
        <Text
          component="span"
          variant="meta"
          c="var(--text-secondary)"
          className="leading-[var(--leading-normal)]"
        >
          {entry.event.note ?? t('detail.contacts.noNote')}
        </Text>
      </div>
      <Text
        component="time"
        title={formatLocalizedDateTime(entry.event.timestamp)}
        variant="meta"
        c="var(--text-tertiary-soft)"
        className="flex-none whitespace-nowrap"
      >
        {when}
      </Text>
    </div>
  );
}
